import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const NortheastCalgary = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighbourhoodWithState = `${neighborhoodName}, ${state}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.northeast-calgary-calgary:About Paragraph">
          Northeast Calgary,{' '}
          <LinkText href="/rental-listings/alberta">Alberta</LinkText>, is a
          vibrant and culturally diverse area known for its strong sense of
          community and variety of amenities. It features the Calgary
          International Airport, making it a hub for travel and commerce.
          Neighborhoods like Bridgeland, Rundle, and Martindale offer a mix of
          residential options, from charming older homes to modern developments.
          The area is rich in parks, with Prairie Winds Park being a popular
          spot for outdoor activities. Northeast Calgary is also home to
          bustling shopping centers like Sunridge Mall and cultural attractions
          such as the Genesis Centre. Excellent public transit links and
          proximity to downtown make it a convenient and dynamic place to live.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo.hood.northeast-calgary-calgary:an apartment to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.northeast-calgary-calgary:Renting In Paragraph">
            Finding {{ filterNameWithFallback }} in {{ neighborhoodName }}{' '}
            requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, signing a lease, and depositing rent. All
            this can add up to a lengthy endeavour if you’re not familiar with
            how to find an apartment in {{ neighborhoodName }}.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodName}
        cityNameWithState={neighbourhoodWithState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$1,317 - $1,675',
          bed2: '$1,755 - $2,340',
          bed3: '$2,350 - $2,800'
        }}
        rentReportCity="Alberta"
        rentReportLink="https://liv.rent/blog/rent-reports/alberta/"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/%e9%98%bf%e5%b0%94%e4%bc%af%e5%a1%94/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.northeast-calgary-calgary:Apartment Hunting Paragraph 1">
            Finding affordable rentals in {{ neighborhoodName }} is easier
            compared to other major cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/toronto">Toronto</LinkText>,
            especially if you’re looking for{' '}
            <LinkText href={links.under1000}>rentals under $1000</LinkText>.
            Along with effective negotiation skills, you may be able to lower
            the rent price further. For helpful tips on negotiating rent, refer
            to our guide on{' '}
            <LinkText href="https://liv.rent/blog/renters/how-to-negotiate-rent/">
              how to negotiate rent
            </LinkText>
            .
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.northeast-calgary-calgary:Apartment Hunting Paragraph 2">
            {{ neighborhoodName }} offers a diverse range of neighborhoods and
            municipalities to choose from when searching for rentals. Depending
            on your preferences, you can explore various rental types, such as{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>, and{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>. Whether
            you’re seeking <LinkText href={links.furnished}>furnished</LinkText>{' '}
            or <LinkText href={links.unfurnished}>unfurnished rentals</LinkText>
            , or even considering{' '}
            <LinkText href={links.longTerm}>long-term</LinkText> or{' '}
            <LinkText href={links.shortTerm}>short-term</LinkText> options,{' '}
            {{ neighborhoodName }} has a variety of choices to suit your needs.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northeast-calgary-calgary:Apartments for rent Paragraph">
              <LinkText href={links.apartment}>Apartments for rent</LinkText>{' '}
              are not common in {{ neighborhoodName }}. Typically, they are more
              suitable for singles, couples, or small families looking for
              modern amenities and less maintenance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northeast-calgary-calgary:Houses for rent Paragraph">
              Detached <LinkText href={links.house}>houses for rent</LinkText>{' '}
              are a popular form of housing in {{ neighborhoodName }}. They are
              suitable for families seeking more space or yard. Both partial
              houses and entire houses for rent are common, with a mix of older
              and newer homes depending on which part of the region you’re
              looking in.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northeast-calgary-calgary:Townhouses for rent Paragraph">
              Townhouses are also common in {{ neighborhoodName }}. They combine
              the best qualities of both apartments and whole houses. Typically
              less expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in {{ neighborhoodName }}
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northeast-calgary-calgary:Basements for rent Paragraph">
              A popular alternative for{' '}
              <LinkText href="https://liv.rent/blog/livrent/top-tips-for-first-time-renters/">
                first-time renters
              </LinkText>{' '}
              and anyone else seeking relatively inexpensive rental housing are
              basements for rent in {{ neighborhoodName }}. Since rent prices
              for apartments and houses can often get too expensive, many
              renters seek out basements for rent as a cheaper alternative.
              Homeowners often rent out their basements as a way to offset their
              mortgage payments. This makes it easy for renters to find
              relatively inexpensive basements for rent in all areas of the
              region.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northeast-calgary-calgary:Condos for rent Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in {{ neighborhoodName }}
              </LinkText>{' '}
              are not a common form of rental housing. These types of rental
              accommodation are perfect for those who like to be in the centre
              of the action, and don’t need too much space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northeast-calgary-calgary:Rooms for rent Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ neighborhoodName }}
              </LinkText>{' '}
              are a great way to experience the best of the region on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northeast-calgary-calgary:Pet friendly rentals Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>
                cat-friendly rental units
              </LinkText>{' '}
              are available throughout the region, as well as properties where
              both are allowed. Keep in mind that some {{ neighborhoodName }}
              neighbourhoods are better suited to those with pets, and adjust
              your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northeast-calgary-calgary:Furnished rentals Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodName }}. Renters seeking furnished
              units should search for these units using filters, but do be
              prepared to pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northeast-calgary-calgary:Short term rentals Paragraph">
              {{ neighborhoodName }} does not carry the distinction of being
              Canada’s largest region and isn’t characterized by a wide range of
              job opportunities or an extensive educational sector. Instead, it
              offers a closer-knit community lifestyle, different from the
              nation’s metropolitan areas.{' '}
              <LinkText href={links.shortTerm}>Short-term rentals</LinkText>{' '}
              aren’t as prevalent in {{ neighborhoodName }} as they are in
              larger cities, particularly because it doesn’t experience the
              typical influx of students or seasonal workers. However, for those
              seeking a quieter, more rural experience or temporary lodging
              during work in the region, the available rental options may
              suffice. The appeal of short-term rentals is the flexibility,
              which is a key factor when{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short terms rentals are suitable
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northeast-calgary-calgary:Cheap rentals Paragraph">
              Compared to other Canadian cities, rent prices in{' '}
              {{ neighborhoodName }} are relatively cheaper. As a result, it
              shouldn’t be too hard to find something within your budget. If
              you’re looking for
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighbourhoodWithState }}
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in{' '}
              {{ neighborhoodName }} where rent is considerably cheaper, so
              exploring these options is a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLivingIn = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.northeast-calgary-calgary:Living In Paragraph">
          Living in Northeast Calgary, Alberta, offers a culturally rich and
          diverse experience. The area is known for its strong community ties
          and affordable housing options. Residents enjoy easy access to the
          Calgary International Airport, making travel convenient. The region
          boasts numerous parks, such as Prairie Winds Park, which offers
          recreational activities and green spaces. Shopping centers like
          Sunridge Mall provide a variety of retail and dining options. The area
          is well-connected by public transit, making commuting hassle-free.
          With its mix of residential neighborhoods, vibrant cultural scene, and
          essential amenities, Northeast Calgary is a welcoming and dynamic
          place to live.
        </Trans>
      </Paragraph>
    )
  }

  const getLocalLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.northeast-calgary-calgary:Local Landmarks Paragraph">
          Northeast Calgary, Alberta, is home to several notable landmarks that
          highlight its cultural and historical significance. The Calgary Zoo is
          a major attraction, offering visitors a chance to see diverse wildlife
          and participate in educational programs. Aero Space Museum of Calgary
          showcases the history of aviation in Canada, with a range of aircraft
          and exhibits. Prairie Winds Park is a popular spot for outdoor
          activities, featuring a wading pool, toboggan hill, and extensive
          green spaces. The Genesis Centre serves as a community hub, providing
          recreational, cultural, and social activities. These landmarks make
          Northeast Calgary a vibrant and engaging place to live and visit.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodName}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: null,
    thingsToDo: null,
    restaurants: null,
    transportation: null,
    whereToStay: null,
    living: getLivingIn(),
    landmarks: getLocalLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
      preventRepeatedCityName
    />
  )
}

export default NortheastCalgary
